import { Chess, type Color, type Move, type PieceSymbol, type Square } from 'chess.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { analisarComMotor, salaAbandonar, salaEnviarChat, salaJogar, salaObterPartida, solicitarJogadaModelo, solicitarJogadaMotor } from '../lib/api';
import { tocarSom } from '../lib/audio';
import { nomeDaCor, sanParaPortugues } from '../lib/notacao';
import { ambosAutomaticos, ehModoDuelo, haHumanoNaPartida, ladoAutomaticoDaCor, nomeDoVencedor } from '../lib/participantes';
import { aplicarIncremento, calcularTempoRestante } from '../lib/relogio';
import { apagarPartida, arquivarPartida, resolverSnapshotPartida, salvarPartida } from '../lib/storage';
import type { ChatMensagemOnline, ConfiguracaoJogo, LadoAutomatico, LinhaAnalisePublica, NivelClassico, PartidaPersistida, Preferencias, ResultadoPartidaInfo } from '../types';
import { capturasDoLado, materialCapturado, vantagemDoLado } from '../lib/material';
import { Icone } from './Icone';
import { Peca } from './Peca';
import { PecasCapturadas } from './PecasCapturadas';
import { Relogio } from './Relogio';
import { Tabuleiro, type SetaTabuleiro } from './Tabuleiro';

/** Avaliações UCI vêm do lado a jogar; convertemos para perspectiva das brancas. */
function evalBrancas(turno: Color, cp?: number, mate?: number): { cp?: number; mate?: number } {
  if (mate != null) return { mate: turno === 'w' ? mate : -mate };
  if (cp == null) return {};
  return { cp: turno === 'w' ? cp : -cp };
}

function formatarEval(cp?: number, mate?: number): string {
  if (mate != null) return mate > 0 ? `M${mate}` : `−M${Math.abs(mate)}`;
  if (cp == null) return '—';
  const peoes = cp / 100;
  return `${peoes > 0 ? '+' : ''}${peoes.toFixed(2)}`;
}

/** 0–100: quanto as brancas estão melhores (barra vertical). */
function percentualEval(cp?: number, mate?: number): number {
  if (mate != null) return mate > 0 ? 97 : 3;
  if (cp == null) return 50;
  const t = Math.max(-900, Math.min(900, cp));
  return 50 + 50 * (2 / (1 + Math.exp(-t / 350)) - 1);
}

function uciParaSeta(uci: string, tipo: SetaTabuleiro['tipo'] = 'dica'): SetaTabuleiro | null {
  if (!uci || uci.length < 4) return null;
  return { de: uci.slice(0, 2), para: uci.slice(2, 4), tipo };
}

interface Props {
  configuracao: ConfiguracaoJogo;
  preferencias: Preferencias;
  restaurada?: PartidaPersistida | null;
  aoAlterarPreferencias: (preferencias: Preferencias) => void;
  aoSair: () => void;
  aoNovaPartida: () => void;
  /** Chamado ao terminar (duelos / série de benchmark). */
  aoConcluir?: (info: ResultadoPartidaInfo) => void;
}
interface Resultado { titulo:string; motivo:string; vencedor:Color|null }
interface Instantaneo { pgn:string; tempos:{w:number;b:number}; iniciada:boolean; orientacao:Color }
interface Confirmacao { titulo:string; texto:string; acao:()=>void; rotulo:string }
const nomesPromocao:Partial<Record<PieceSymbol,string>>={q:'dama',r:'torre',b:'bispo',n:'cavalo'};

function carregarJogo(partida?: PartidaPersistida|null): Chess {
  const jogo=new Chess();
  if(partida?.pgn){ try{ jogo.loadPgn(partida.pgn); }catch{ if(partida.fen) jogo.load(partida.fen); } }
  else if(partida?.fen){ try{ jogo.load(partida.fen); }catch{ /* posição inicial */ } }
  return jogo;
}
function identificarResultado(jogo:Chess,config:ConfiguracaoJogo):Resultado|null{
  if(jogo.isCheckmate()){
    const vencedor=jogo.turn()==='w'?'b':'w';
    const quem=nomeDoVencedor(config,vencedor);
    return{titulo:quem&&quem!=='jogador'?`Vitória de ${quem}`:`Vitória das ${nomeDaCor(vencedor).toLowerCase()}`,motivo:'Xeque-mate',vencedor};
  }
  if(jogo.isStalemate())return{titulo:'Empate',motivo:'Afogamento',vencedor:null};
  if(jogo.isThreefoldRepetition())return{titulo:'Empate',motivo:'Repetição tripla',vencedor:null};
  if(jogo.isInsufficientMaterial())return{titulo:'Empate',motivo:'Material insuficiente',vencedor:null};
  if(jogo.isDrawByFiftyMoves())return{titulo:'Empate',motivo:'Regra dos cinquenta lances',vencedor:null};
  if(jogo.isDraw())return{titulo:'Empate',motivo:'A posição não permite continuação',vencedor:null};
  return null;
}

function rotuloCompetidor(config:ConfiguracaoJogo,cor:Color):string{
  const auto=ladoAutomaticoDaCor(config,cor);
  if(auto){
    // Usa o nome montado na preparação (inclui papel A/B + motor + nível).
    const base=auto.nome?.trim()||(auto.tipo==='lc0'?'Leela':auto.tipo==='stockfish'?'Stockfish':'Modelo');
    return base.length>28?`${base.slice(0,26)}…`:base;
  }
  const nome=cor==='w'?config.nomeBrancas:config.nomePretas;
  if(nome==='Você')return 'Você';
  return nome.length>28?`${nome.slice(0,26)}…`:nome;
}

export function Partida({configuracao,preferencias,restaurada,aoAlterarPreferencias,aoSair,aoNovaPartida,aoConcluir}:Props){
  // Snapshot estável no mount: prop restaurada OU localStorage (HMR / remount).
  const snapshotRef=useRef<PartidaPersistida|null|undefined>(undefined);
  if(snapshotRef.current===undefined){
    snapshotRef.current=resolverSnapshotPartida(restaurada,configuracao,preferencias.nomeJogador);
  }
  const snapshot=snapshotRef.current;
  const [jogo] = useState(()=>carregarJogo(snapshot));
  const [versao,setVersao]=useState(0);
  const [selecionada,setSelecionada]=useState<Square|null>(null);
  const [destinos,setDestinos]=useState<Array<{para:Square;captura:boolean}>>([]);
  const historicoInicial=jogo.history({verbose:true});
  const [ultimaJogada,setUltimaJogada]=useState<{de:string;para:string}|undefined>(()=>{const m=historicoInicial.at(-1);return m?{de:m.from,para:m.to}:undefined;});
  /** Arraste já moveu a peça com o dedo/cursor — não repete o voo animado. */
  const pularAnimacaoRef=useRef(false);
  const [pularAnimacao,setPularAnimacao]=useState(false);
  const tempoInicial=configuracao.controle.minutos*60_000;
  const temposRestaurados=useMemo(()=>{if(!snapshot)return{w:tempoInicial,b:tempoInicial};const valores={w:snapshot.tempoBrancas,b:snapshot.tempoPretas};if(snapshot.iniciada&&snapshot.ativa&&configuracao.controle.minutos>0){valores[jogo.turn()]=Math.max(0,valores[jogo.turn()]-(Date.now()-snapshot.salvaEm));}return valores;},[]);
  const [tempos,setTempos]=useState<{w:number;b:number}>(temposRestaurados);
  const [iniciada,setIniciada]=useState(snapshot?.iniciada??false);
  const [orientacao,setOrientacao]=useState<Color>(snapshot?.orientacao??(ehModoDuelo(configuracao.modo)||configuracao.apenasAssistir?'w':configuracao.modo==='local'?'w':configuracao.corJogador));
  const [agora,setAgora]=useState(Date.now());
  const [resultado,setResultado]=useState<Resultado|null>(()=>identificarResultado(jogo,configuracao));
  const [pensando,setPensando]=useState(false);
  const [aviso,setAviso]=useState<string|null>(null);
  const [promocao,setPromocao]=useState<{de:Square;para:Square}|null>(null);
  const [confirmacao,setConfirmacao]=useState<Confirmacao|null>(null);
  const [painelMovel,setPainelMovel]=useState(false);
  const [maisAberto,setMaisAberto]=useState(false);
  const [copiado,setCopiado]=useState<string|null>(null);
  const [nomeUsuario,setNomeUsuario]=useState(preferencias.nomeJogador?.trim()||'');
  const [partidaArquivada,setPartidaArquivada]=useState(false);
  const [analiseLigada,setAnaliseLigada]=useState(false);
  const [analisando,setAnalisando]=useState(false);
  const [linhasAnalise,setLinhasAnalise]=useState<LinhaAnalisePublica[]>([]);
  const [nomeMotorAnalise,setNomeMotorAnalise]=useState<string|null>(null);
  const [setaDica,setSetaDica]=useState<SetaTabuleiro|null>(null);
  const [textoImport,setTextoImport]=useState('');
  const [chatMsgs,setChatMsgs]=useState<ChatMensagemOnline[]>([]);
  const [chatTexto,setChatTexto]=useState('');
  const [chatEnviando,setChatEnviando]=useState(false);
  const chatFimRef=useRef<HTMLDivElement|null>(null);
  const marcoRef=useRef(Date.now()); const workerRef=useRef<Worker|null>(null); const abortRef=useRef<AbortController|null>(null); const requisicaoAtivaRef=useRef<string|null>(null); const instantaneos=useRef<Instantaneo[]>([]); const fenRef=useRef(jogo.fen()); const resultadoRef=useRef(resultado); const idPartida=useRef(snapshot?.idPartida??crypto.randomUUID()); const concluiuRef=useRef(false); const analiseSeqRef=useRef(0);
  const semRelogio=configuracao.controle.minutos===0;
  const online=configuracao.modo==='online'&&Boolean(configuracao.onlinePartidaId&&configuracao.onlineClienteId);
  const souEspectadorOnline=online&&Boolean(configuracao.apenasAssistir);
  const souJogadorOnline=online&&!souEspectadorOnline;
  const duelo=ehModoDuelo(configuracao.modo)||ambosAutomaticos(configuracao)||Boolean(configuracao.apenasAssistir);
  const ladoTurno=online?null:ladoAutomaticoDaCor(configuracao,jogo.turn());
  const turnoOnlineAdversario=online&&!souEspectadorOnline&&jogo.turn()!==configuracao.corJogador;
  const turnoAutomatico=Boolean(ladoTurno)||(duelo&&Boolean(configuracao.apenasAssistir))||turnoOnlineAdversario||souEspectadorOnline;

  const tempoEfetivo=useCallback((cor:Color,instante=Date.now())=>{
    const desconto=!semRelogio&&iniciada&&!resultadoRef.current&&jogo.turn()===cor?instante-marcoRef.current:0;
    return calcularTempoRestante(tempos[cor], marcoRef.current, instante, desconto > 0);
  },[iniciada,jogo,semRelogio,tempos]);

  useEffect(()=>{resultadoRef.current=resultado;},[resultado]);
  useEffect(()=>{fenRef.current=jogo.fen();},[jogo,versao]);
  useEffect(()=>{const timer=window.setInterval(()=>setAgora(Date.now()),100);return()=>window.clearInterval(timer);},[]);
  useEffect(()=>()=>{workerRef.current?.terminate();abortRef.current?.abort();},[]);

  const encerrar=useCallback((fim:Resultado & {encerrarSerie?: boolean})=>{
    let final:Resultado={titulo:fim.titulo,motivo:fim.motivo,vencedor:fim.vencedor};
    if(fim.vencedor){
      const quem=nomeDoVencedor(configuracao,fim.vencedor);
      if(quem&&quem!=='jogador') final={...final,titulo:`Vitória de ${quem}`};
      else if(quem==='jogador') final={...final,titulo:'Vitória do jogador'};
    }
    requisicaoAtivaRef.current=null;setResultado(final);resultadoRef.current=final;setPensando(false);abortRef.current?.abort();apagarPartida(preferencias.nomeJogador);
    const espectador=duelo||configuracao.apenasAssistir;
    const som=final.vencedor===null?'empate':espectador?'vitoria':final.vencedor===configuracao.corJogador||configuracao.modo==='local'?'vitoria':'derrota';
    tocarSom(som,preferencias.volume,preferencias.somAtivo);
    if(!concluiuRef.current&&aoConcluir){
      concluiuRef.current=true;
      aoConcluir({
        vencedor:final.vencedor,
        motivo:final.motivo,
        pgn:jogo.pgn({maxWidth:120,newline:'\n'}),
        lances:jogo.history().length,
        encerrarSerie:Boolean(fim.encerrarSerie)
      });
    }
  },[aoConcluir,configuracao,duelo,jogo,preferencias]);

  useEffect(()=>{if(semRelogio||!iniciada||resultado)return;const turno=jogo.turn();const restante=tempoEfetivo(turno,agora);if(restante<=0){const vencedor=turno==='w'?'b':'w';const quem=nomeDoVencedor(configuracao,vencedor);encerrar({titulo:quem&&quem!=='jogador'?`Vitória de ${quem}`:`Vitória das ${nomeDaCor(vencedor).toLowerCase()}`,motivo:'Vitória por tempo',vencedor});}},[agora,configuracao,encerrar,iniciada,jogo,resultado,semRelogio,tempoEfetivo]);

  const salvarEstado=useCallback(()=>{
    if(resultadoRef.current)return;
    if(configuracao.apenasAssistir)return; // espectador não grava partida local
    const dono=preferencias.nomeJogador?.trim();
    if(!dono)return;
    const instante=Date.now();
    salvarPartida({
      versao:1,
      idPartida:idPartida.current,
      configuracao,
      pgn:jogo.pgn({maxWidth:120,newline:'\n'}),
      fen:jogo.fen(),
      orientacao,
      tempoBrancas:tempoEfetivo('w',instante),
      tempoPretas:tempoEfetivo('b',instante),
      iniciada,
      ativa:true,
      salvaEm:instante,
      donoSessao:dono
    },dono);
  },[configuracao,iniciada,jogo,orientacao,preferencias.nomeJogador,tempoEfetivo]);
  useEffect(()=>{
    const timer=window.setInterval(salvarEstado,800);
    const aoSairPagina=()=>salvarEstado();
    window.addEventListener('beforeunload',aoSairPagina);
    window.addEventListener('pagehide',aoSairPagina);
    document.addEventListener('visibilitychange',()=>{if(document.visibilityState==='hidden')salvarEstado();});
    return()=>{
      window.clearInterval(timer);
      window.removeEventListener('beforeunload',aoSairPagina);
      window.removeEventListener('pagehide',aoSairPagina);
      salvarEstado(); // persiste no unmount (HMR / troca de tela)
    };
  },[salvarEstado]);
  useEffect(()=>{salvarEstado();},[versao,orientacao,salvarEstado]);

  function registrarInstantaneo(){instantaneos.current.push({pgn:jogo.pgn({maxWidth:120,newline:'\n'}),tempos:{w:tempoEfetivo('w'),b:tempoEfetivo('b')},iniciada,orientacao});}
  function anunciarMovimento(movimento:Move){let som:'movimento'|'captura'|'xeque'|'roque'|'promocao'='movimento';if(movimento.promotion)som='promocao';else if(movimento.flags.includes('k')||movimento.flags.includes('q'))som='roque';else if(movimento.captured)som='captura';else if(jogo.inCheck())som='xeque';tocarSom(som,preferencias.volume,preferencias.somAtivo);}

  const aplicarEstadoOnline=useCallback((part:{fen:string;pgn:string;status:string;resultado?:string;vencedor?:Color|null;ultimoLance?:{de:string;para:string}})=>{
    try{
      if(part.pgn?.trim()){jogo.reset();jogo.loadPgn(part.pgn);}
      else jogo.load(part.fen);
    }catch{
      try{jogo.load(part.fen);}catch{/* ignore */}
    }
    fenRef.current=jogo.fen();
    setIniciada(true);
    setSelecionada(null);setDestinos([]);
    if(part.ultimoLance)setUltimaJogada({de:part.ultimoLance.de,para:part.ultimoLance.para});
    else{
      const u=jogo.history({verbose:true}).at(-1);
      setUltimaJogada(u?{de:u.from,para:u.to}:undefined);
    }
    setVersao(v=>v+1);
    if(part.status==='terminada'&&!resultadoRef.current){
      const vencedor=part.vencedor===undefined?null:part.vencedor;
      const quem=nomeDoVencedor(configuracao,vencedor);
      encerrar({
        titulo:vencedor===null?'Empate':quem&&quem!=='jogador'?`Vitória de ${quem}`:`Vitória das ${nomeDaCor(vencedor).toLowerCase()}`,
        motivo:part.resultado||'Partida encerrada',
        vencedor
      });
    }
  },[configuracao,encerrar,jogo]);

  const realizarMovimento=useCallback((de:Square,para:Square,pecaPromocao?:PieceSymbol):boolean=>{
    if(resultadoRef.current)return false;
    if(online&&jogo.turn()!==configuracao.corJogador)return false;
    const origem=jogo.turn(); const instante=Date.now(); const restante=tempoEfetivo(origem,instante);
    if(!semRelogio&&iniciada&&restante<=0)return false;
    const candidatos=jogo.moves({square:de,verbose:true}).filter(m=>m.to===para);
    if(!candidatos.length)return false;
    if(candidatos.some(m=>m.promotion)&&!pecaPromocao){setPromocao({de,para});return false;}

    const pular=pularAnimacaoRef.current;
    pularAnimacaoRef.current=false;

    if(online&&configuracao.onlinePartidaId&&configuracao.onlineClienteId){
      setPensando(true);
      void salaJogar(configuracao.onlinePartidaId,configuracao.onlineClienteId,de,para,pecaPromocao)
        .then((part)=>{
          registrarInstantaneo();
          setPularAnimacao(pular);
          aplicarEstadoOnline(part);
          const hist=jogo.history({verbose:true});
          const ult=hist.at(-1);
          if(ult)anunciarMovimento(ult);
        })
        .catch((erro)=>{setAviso(erro instanceof Error?erro.message:'Lance online recusado.');})
        .finally(()=>setPensando(false));
      setPromocao(null);
      return true;
    }

    registrarInstantaneo();
    let movimento:Move;
    try{movimento=jogo.move({from:de,to:para,promotion:pecaPromocao??'q'});}catch{return false;}
    const atualizados={...tempos,[origem]:semRelogio?tempos[origem]:aplicarIncremento(restante,configuracao.controle.incremento)}; setTempos(atualizados); setIniciada(true); marcoRef.current=instante; setAgora(instante);
    setPularAnimacao(pular);
    setSelecionada(null);setDestinos([]);setUltimaJogada({de:movimento.from,para:movimento.to});setPromocao(null);setAviso(null);setVersao(v=>v+1);
    if(configuracao.modo==='local'&&configuracao.inverterAutomaticamente)setOrientacao(jogo.turn());
    anunciarMovimento(movimento); const fim=identificarResultado(jogo,configuracao); if(fim)encerrar(fim); return true;
  },[aplicarEstadoOnline,configuracao,encerrar,iniciada,jogo,online,preferencias,semRelogio,tempoEfetivo,tempos]);

  function selecionarCasa(casa:Square){if(pensando||resultado||turnoAutomatico)return;if(online&&jogo.turn()!==configuracao.corJogador)return;const peca=jogo.get(casa);if(selecionada){if(destinos.some(d=>d.para===casa)){realizarMovimento(selecionada,casa);return;}if(peca?.color===jogo.turn()){setSelecionada(casa);setDestinos(jogo.moves({square:casa,verbose:true}).map(m=>({para:m.to,captura:Boolean(m.captured)})));return;}setSelecionada(null);setDestinos([]);return;}if(peca?.color===jogo.turn()){setSelecionada(casa);setDestinos(jogo.moves({square:casa,verbose:true}).map(m=>({para:m.to,captura:Boolean(m.captured)})));}}
  function moverArrastando(de:Square,para:Square){if(!pensando&&!resultado&&!turnoAutomatico){pularAnimacaoRef.current=true;realizarMovimento(de,para);}}

  // Sincroniza partida online (lance do adversário / fim de jogo / chat).
  useEffect(()=>{
    if(!online||!configuracao.onlinePartidaId||resultado)return;
    const id=configuracao.onlinePartidaId;
    const cliente=configuracao.onlineClienteId;
    const timer=window.setInterval(()=>{
      void salaObterPartida(id,cliente).then((part)=>{
        if(Array.isArray(part.chat)) setChatMsgs(part.chat);
        if(part.fen!==fenRef.current){
          const antes=fenRef.current;
          setPularAnimacao(false);
          aplicarEstadoOnline(part);
          if(antes!==part.fen){
            const ult=jogo.history({verbose:true}).at(-1);
            if(ult)anunciarMovimento(ult);
          }
        }else if(part.status==='terminada'&&!resultadoRef.current){
          aplicarEstadoOnline(part);
        }
      }).catch(()=>{/* rede instável */});
    },900);
    return()=>window.clearInterval(timer);
  },[aplicarEstadoOnline,configuracao.onlineClienteId,configuracao.onlinePartidaId,jogo,online,resultado]);

  useEffect(()=>{
    chatFimRef.current?.scrollIntoView({behavior:'smooth',block:'end'});
  },[chatMsgs.length]);

  async function enviarChat(){
    if(!souJogadorOnline||!configuracao.onlinePartidaId||!configuracao.onlineClienteId)return;
    const texto=chatTexto.trim();
    if(!texto||chatEnviando)return;
    setChatEnviando(true);
    try{
      const part=await salaEnviarChat(configuracao.onlinePartidaId,configuracao.onlineClienteId,texto);
      setChatMsgs(part.chat??[]);
      setChatTexto('');
    }catch(e){
      setAviso(e instanceof Error?e.message:'Não foi possível enviar a mensagem.');
    }finally{
      setChatEnviando(false);
    }
  }

  const solicitarStockfishServidor=useCallback(async(fen:string,nivel:NivelClassico,signal:AbortSignal,tempoDisponivelMs?:number):Promise<string>=>{
    const r=await solicitarJogadaMotor(fen,nivel,{motor:'stockfish',tempoDisponivelMs,idPartida:idPartida.current},signal);
    return r.jogada;
  },[]);

  const solicitarLado=useCallback(async(lado:LadoAutomatico,fen:string,signal:AbortSignal,idPosicao:string):Promise<string>=>{
    const nivel=(lado.nivel??configuracao.nivel) as NivelClassico;
    const tempoDisponivel=tempoEfetivo(jogo.turn());
    if(lado.tipo==='modelo'&&lado.modeloPublicoId){
      const legais=jogo.moves({verbose:true});
      try{
        const resposta=await solicitarJogadaModelo({
          modeloPublicoId:lado.modeloPublicoId,fen,pgn:jogo.pgn({maxWidth:120,newline:'\n'}),cor:jogo.turn(),
          jogadasLegais:legais.map(m=>`${m.from}${m.to}${m.promotion||''}`),jogadasSan:legais.map(m=>m.san),
          estilo:lado.estilo??configuracao.estilo,
          assistencia:configuracao.assistenciaModelo??'regio',
          tempoBrancas:tempoEfetivo('w'),tempoPretas:tempoEfetivo('b'),
          controle:configuracao.controle.nome,idPartida:idPartida.current,idPosicao
        },signal);
        if(resposta.usouAlternativa)setAviso(resposta.motivoAlternativa||'O Stockfish ajustou a escolha do modelo.');
        return resposta.jogada;
      }catch(erro){
        if(signal.aborted) throw erro;
        setAviso(erro instanceof Error?`Modelo falhou: ${erro.message}. Stockfish assumiu.`:'Modelo falhou. Stockfish assumiu.');
        return solicitarStockfishServidor(fen,nivel==='maximo'||nivel==='2500'?nivel:'2000',signal,tempoDisponivel);
      }
    }
    if(lado.tipo==='lc0'){
      try{
        const r=await solicitarJogadaMotor(fen,nivel,{motor:'lc0',tempoDisponivelMs:tempoDisponivel,idPartida:idPartida.current,idPosicao},signal);
        return r.jogada;
      }catch(erro){
        if(signal.aborted) throw erro;
        setAviso(erro instanceof Error?`Leela falhou: ${erro.message}. Stockfish assumiu.`:'Leela falhou. Stockfish assumiu.');
        return solicitarStockfishServidor(fen,nivel,signal,tempoDisponivel);
      }
    }
    return solicitarStockfishServidor(fen,nivel,signal,tempoDisponivel);
  },[configuracao.assistenciaModelo,configuracao.controle.nome,configuracao.estilo,configuracao.nivel,jogo,solicitarStockfishServidor,tempoEfetivo]);

  useEffect(()=>{
    if(online)return;
    const lado=ladoAutomaticoDaCor(configuracao,jogo.turn());
    if(!lado||resultado||pensando||requisicaoAtivaRef.current)return;
    const limite=configuracao.limiteLances??400;
    if(jogo.history().length>=limite){
      encerrar({titulo:'Empate',motivo:`Limite de ${limite} lances atingido`,vencedor:null});
      return;
    }
    const fenOrigem=jogo.fen();const idPosicao=crypto.randomUUID();const controle=new AbortController();
    requisicaoAtivaRef.current=idPosicao;abortRef.current=controle;setPensando(true);setAviso(null);
    const executar=async()=>{
      try{
        const uci=await solicitarLado(lado,fenOrigem,controle.signal,idPosicao);
        if(controle.signal.aborted||requisicaoAtivaRef.current!==idPosicao||fenRef.current!==fenOrigem)return;
        const de=uci.slice(0,2) as Square;const para=uci.slice(2,4) as Square;const promocaoUci=uci[4] as PieceSymbol|undefined;
        const legal=jogo.moves({square:de,verbose:true}).some(m=>m.to===para&&(!m.promotion||m.promotion===promocaoUci));
        if(!legal)throw new Error('A jogada recebida não é legal nesta posição.');
        realizarMovimento(de,para,promocaoUci);
      }catch(erro){
        if(!controle.signal.aborted)setAviso(erro instanceof Error?erro.message:'Não foi possível concluir a jogada automática.');
      }finally{
        if(requisicaoAtivaRef.current===idPosicao){requisicaoAtivaRef.current=null;if(!controle.signal.aborted)setPensando(false);}
      }
    };
    const atraso=window.setTimeout(()=>void executar(),duelo?120:220);
    return()=>{window.clearTimeout(atraso);if(requisicaoAtivaRef.current===idPosicao){requisicaoAtivaRef.current=null;controle.abort();}};
  },[versao,resultado]);

  function desfazer(){if(!configuracao.permitirDesfazer||!instantaneos.current.length)return;abortRef.current?.abort();requisicaoAtivaRef.current=null;setPensando(false);const quantidade=configuracao.modo==='local'?1:Math.min(2,instantaneos.current.length);const indice=Math.max(0,instantaneos.current.length-quantidade);const alvo=instantaneos.current[indice];instantaneos.current=instantaneos.current.slice(0,indice);if(alvo.pgn)jogo.loadPgn(alvo.pgn);else jogo.reset();setTempos(alvo.tempos);setIniciada(alvo.iniciada);setOrientacao(alvo.orientacao);marcoRef.current=Date.now();setResultado(null);resultadoRef.current=null;setSelecionada(null);setDestinos([]);const ultimo=jogo.history({verbose:true}).at(-1);setUltimaJogada(ultimo?{de:ultimo.from,para:ultimo.to}:undefined);setVersao(v=>v+1);}
  function desistir(){
    if(souEspectadorOnline){
      aoSair();
      return;
    }
    if(online&&configuracao.onlinePartidaId&&configuracao.onlineClienteId){
      void salaAbandonar(configuracao.onlinePartidaId,configuracao.onlineClienteId)
        .then((part)=>aplicarEstadoOnline(part))
        .catch(()=>{
          const vencedor=configuracao.corJogador==='w'?'b':'w';
          encerrar({titulo:`Vitória de ${nomeDoVencedor(configuracao,vencedor)||'adversário'}`,motivo:'Desistência',vencedor});
        });
      return;
    }
    if(duelo){encerrar({titulo:'Partida interrompida',motivo:'Encerrada pelo espectador',vencedor:null});return;}
    const perdedor=configuracao.modo==='local'?jogo.turn():configuracao.corJogador;const vencedor=perdedor==='w'?'b':'w';
    const quem=nomeDoVencedor(configuracao,vencedor);
    encerrar({titulo:quem&&quem!=='jogador'?`Vitória de ${quem}`:`Vitória das ${nomeDaCor(vencedor).toLowerCase()}`,motivo:'Desistência',vencedor});
  }
  /** Encerra a série/benchmark inteiro (não só a partida atual). */
  function encerrarBenchmark(){
    encerrar({
      titulo:'Benchmark interrompido',
      motivo:'Benchmark encerrado pelo espectador',
      vencedor:null,
      encerrarSerie:true
    });
  }
  function empate(){encerrar({titulo:'Empate',motivo:'Empate por acordo',vencedor:null});}
  async function copiar(texto:string,tipo:string){try{await navigator.clipboard.writeText(texto);setCopiado(tipo);window.setTimeout(()=>setCopiado(null),1600);}catch{setAviso('O navegador não permitiu copiar automaticamente.');}}
  function baixarPgn(){const pgn=jogo.pgn({maxWidth:80,newline:'\n'});const blob=new Blob([pgn],{type:'application/x-chess-pgn;charset=utf-8'});const url=URL.createObjectURL(blob);const a=document.createElement('a');a.href=url;a.download=`xeque-regio-${new Date().toISOString().slice(0,10)}.pgn`;a.click();URL.revokeObjectURL(url);}
  function telaCheia(){const acao=document.fullscreenElement?document.exitFullscreen():document.documentElement.requestFullscreen();void acao.catch(()=>setAviso('A tela inteira não está disponível neste navegador.'));}
  function sair(){salvarEstado();requisicaoAtivaRef.current=null;abortRef.current?.abort();aoSair();}

  const rodarAnalise=useCallback(async(forcar=false)=>{
    if(resultadoRef.current)return;
    if(pensando&&!forcar)return;
    const fen=jogo.fen();
    const seq=++analiseSeqRef.current;
    setAnalisando(true);
    try{
      const r=await analisarComMotor(fen,900,3);
      if(analiseSeqRef.current!==seq||fenRef.current!==fen)return;
      setLinhasAnalise(r.linhas);
      setNomeMotorAnalise(r.nomeMotor);
      if(r.melhorJogada){
        const seta=uciParaSeta(r.melhorJogada,'dica');
        if(seta) setSetaDica(seta);
      }
    }catch(erro){
      if(analiseSeqRef.current===seq){
        setAviso(erro instanceof Error?erro.message:'Não foi possível analisar a posição.');
      }
    }finally{
      if(analiseSeqRef.current===seq) setAnalisando(false);
    }
  },[jogo,pensando]);

  useEffect(()=>{
    if(!analiseLigada||resultado||pensando)return;
    const t=window.setTimeout(()=>{void rodarAnalise();},380);
    return()=>window.clearTimeout(t);
  },[analiseLigada,versao,resultado,pensando,rodarAnalise]);

  useEffect(()=>{
    if(!analiseLigada){ setLinhasAnalise([]); setSetaDica(null); setNomeMotorAnalise(null); }
  },[analiseLigada]);

  function pedirDica(){
    setAnaliseLigada(true);
    void rodarAnalise(true);
  }

  function importarPosicao(){
    const bruto=textoImport.trim();
    if(!bruto){setAviso('Cole um FEN ou PGN para carregar.');return;}
    if(pensando){setAviso('Aguarde o motor terminar antes de importar.');return;}
    const teste=new Chess();
    let ok=false;
    try{
      if(bruto.includes('[')||bruto.includes('1.')||/\d+\./.test(bruto)){
        teste.loadPgn(bruto);
        ok=true;
      }else{
        teste.load(bruto);
        ok=true;
      }
    }catch{
      try{teste.load(bruto);ok=true;}catch{ok=false;}
    }
    if(!ok){setAviso('FEN ou PGN inválido.');return;}
    registrarInstantaneo();
    abortRef.current?.abort();
    requisicaoAtivaRef.current=null;
    setPensando(false);
    if(teste.history().length) jogo.loadPgn(teste.pgn());
    else jogo.load(teste.fen());
    setResultado(null);resultadoRef.current=null;
    setSelecionada(null);setDestinos([]);
    const ultimo=jogo.history({verbose:true}).at(-1);
    setUltimaJogada(ultimo?{de:ultimo.from,para:ultimo.to}:undefined);
    setIniciada(true);
    marcoRef.current=Date.now();
    setSetaDica(null);setLinhasAnalise([]);
    setTextoImport('');
    setAviso('Posição carregada.');
    setVersao(v=>v+1);
  }

  const turnoAtual=jogo.turn();
  const linhaTopo=linhasAnalise[0];
  const evalW=linhaTopo?evalBrancas(turnoAtual,linhaTopo.avaliacaoCp,linhaTopo.mate):{};
  const pctEval=percentualEval(evalW.cp,evalW.mate);
  const setasTabuleiro=useMemo(()=>{
    const lista:SetaTabuleiro[]=[];
    if(setaDica) lista.push(setaDica);
    else if(analiseLigada&&linhasAnalise[0]){
      const s=uciParaSeta(linhasAnalise[0].jogada,'dica');
      if(s) lista.push(s);
    }
    if(analiseLigada&&linhasAnalise[1]){
      const s=uciParaSeta(linhasAnalise[1].jogada,'pv');
      if(s) lista.push({...s,opacidade:.55});
    }
    return lista;
  },[setaDica,analiseLigada,linhasAnalise]);

  function montarArquivo(nomeJogador:string){
    if(!resultado)return null;
    const quem=resultado.vencedor?nomeDoVencedor(configuracao,resultado.vencedor):null;
    const nomeVencedor=resultado.vencedor===null?'Empate':(quem&&quem!=='jogador'?quem:nomeJogador);
    return {
      id:idPartida.current,
      salvaEm:Date.now(),
      nomeJogador,
      nomeVencedor,
      vencedor:resultado.vencedor,
      motivo:resultado.motivo,
      pgn:jogo.pgn({maxWidth:120,newline:'\n'}),
      modo:configuracao.modo,
      nomeBrancas:configuracao.nomeBrancas,
      nomePretas:configuracao.nomePretas,
      lances:jogo.history().length
    };
  }

  function salvarComNome(){
    if(!resultado)return;
    const nome=nomeUsuario.trim()||preferencias.nomeJogador?.trim()||'';
    if(!nome){setAviso('Informe seu nome para arquivar a partida.');return;}
    const arquivo=montarArquivo(nome);
    if(!arquivo)return;
    arquivarPartida(arquivo);
    aoAlterarPreferencias({...preferencias,nomeJogador:nome});
    setPartidaArquivada(true);
  }

  // Conta logada: arquiva automaticamente no histórico do jogador.
  useEffect(()=>{
    if(!resultado||partidaArquivada||!haHumanoNaPartida(configuracao)||configuracao.apenasAssistir)return;
    const nome=preferencias.nomeJogador?.trim();
    if(!nome)return;
    const arquivo=montarArquivo(nome);
    if(!arquivo)return;
    arquivarPartida(arquivo);
    setNomeUsuario(nome);
    setPartidaArquivada(true);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[resultado,partidaArquivada,configuracao,preferencias.nomeJogador]);

  // Arquiva automaticamente duelos (só motores/modelos); sem conta humana.
  useEffect(()=>{
    if(!resultado||partidaArquivada||haHumanoNaPartida(configuracao))return;
    const quem=resultado.vencedor?nomeDoVencedor(configuracao,resultado.vencedor):null;
    arquivarPartida({
      id:idPartida.current,
      salvaEm:Date.now(),
      nomeJogador:preferencias.nomeJogador?.trim()||'Assistido',
      nomeVencedor:resultado.vencedor===null?'Empate':(quem||'Desconhecido'),
      vencedor:resultado.vencedor,
      motivo:resultado.motivo,
      pgn:jogo.pgn({maxWidth:120,newline:'\n'}),
      modo:configuracao.modo,
      nomeBrancas:configuracao.nomeBrancas,
      nomePretas:configuracao.nomePretas,
      lances:jogo.history().length
    });
    setPartidaArquivada(true);
  },[resultado,partidaArquivada,configuracao,jogo,preferencias.nomeJogador]);

  const precisaNomeUsuario=Boolean(resultado&&haHumanoNaPartida(configuracao)&&!partidaArquivada&&!preferencias.nomeJogador?.trim());
  const historico=jogo.history({verbose:true});const pares=Array.from({length:Math.ceil(historico.length/2)},(_,i)=>({numero:i+1,brancas:historico[i*2],pretas:historico[i*2+1]}));
  const material=useMemo(()=>materialCapturado(jogo.history({verbose:true})),[jogo,versao]);
  const tempoBrancas=tempoEfetivo('w',agora);const tempoPretas=tempoEfetivo('b',agora);const corSuperior:Color=orientacao==='w'?'b':'w';const corInferior:Color=orientacao;
  const nome=(cor:Color)=>rotuloCompetidor(configuracao,cor);
  const nomeBrancasHist=rotuloCompetidor(configuracao,'w');
  const nomePretasHist=rotuloCompetidor(configuracao,'b');
  const ladoPensando=ladoAutomaticoDaCor(configuracao,jogo.turn());
  const textoPensando=ladoPensando
    ?(ladoPensando.tipo==='modelo'?`${ladoPensando.nome} está pensando.`
      :ladoPensando.tipo==='lc0'?'A Leela está calculando na GPU.':'O Stockfish está calculando.')
    :'Calculando…';
  const rotuloMotor=
    configuracao.modo==='local'?'PARTIDA LOCAL'
    :souEspectadorOnline?'ASSISTINDO ONLINE'
    :configuracao.modo==='online'?(configuracao.onlinePermitirEspectadores?'ONLINE · PÚBLICA':'ONLINE · PRIVADA')
    :configuracao.modo==='benchmark'?'BENCHMARK · ASSISTIDO'
    :configuracao.modo==='duelo-motores'?'DUELO DE MOTORES'
    :configuracao.modo==='duelo-modelos'?'DUELO DE MODELOS'
    :configuracao.modo==='modelo'?'MODELO DE LINGUAGEM'
    :(configuracao.motorLocal==='lc0'?'LEELA · GPU LOCAL':'STOCKFISH 18');
  const textoPensandoOnline=online?(souEspectadorOnline?'Você está assistindo.':turnoOnlineAdversario?'Aguardando o adversário…':pensando?'Enviando lance…':''):'';
  const corDoPapel=(papel:'A'|'B')=>{
    if(configuracao.brancasAuto?.papel===papel)return ' · brancas';
    if(configuracao.pretasAuto?.papel===papel)return ' · pretas';
    return '';
  };
  const status=resultado?resultado.motivo
    :(online&&textoPensandoOnline)?textoPensandoOnline
    :pensando?textoPensando
    :(jogo.inCheck()?`Xeque às ${nomeDaCor(jogo.turn()).toLowerCase()}.`:`Vez das ${nomeDaCor(jogo.turn()).toLowerCase()}.`);

  return <main className="tela-partida">
    <header className="barra-partida"><button type="button" className="marca-partida" onClick={sair} aria-label="Voltar ao início"><Icone nome="coroa" tamanho={22}/><span>XADREZ</span></button><div className="estado-barra"><i className={jogo.turn()==='w'?'ponto-claro':'ponto-escuro'}/><span>{status}</span></div><nav><button type="button" className="botao-icone" onClick={()=>aoAlterarPreferencias({...preferencias,somAtivo:!preferencias.somAtivo})} aria-label={preferencias.somAtivo?'Silenciar sons':'Ativar sons'}><Icone nome={preferencias.somAtivo?'som':'mudo'}/></button><button type="button" className="botao-icone" onClick={telaCheia} aria-label="Alternar tela inteira"><Icone nome="tela"/></button><button type="button" className="botao-icone" onClick={()=>setMaisAberto(v=>!v)} aria-label="Mais opções"><Icone nome="mais"/></button></nav>
      {maisAberto&&<div className="menu-partida"><button type="button" onClick={()=>{setOrientacao(o=>o==='w'?'b':'w');setMaisAberto(false);}}><Icone nome="girar"/> Virar tabuleiro</button><button type="button" onClick={()=>{pedirDica();setMaisAberto(false);}}><Icone nome="avancar"/> Pedir dica</button><button type="button" onClick={()=>{setAnaliseLigada(v=>!v);setMaisAberto(false);}}><Icone nome="olho"/> {analiseLigada?'Desligar análise':'Analisar posição'}</button><button type="button" disabled={!configuracao.permitirDesfazer||!instantaneos.current.length} onClick={()=>{desfazer();setMaisAberto(false);}}><Icone nome="desfazer"/> Desfazer</button><button type="button" onClick={()=>{setPainelMovel(true);setMaisAberto(false);}}><Icone nome="historico"/> Histórico e notação</button><button type="button" onClick={()=>{setMaisAberto(false);setConfirmacao({titulo:configuracao.modo==='benchmark'?'Encerrar esta partida?':'Desistir da partida?',texto:configuracao.modo==='benchmark'?'Só esta partida será interrompida. O benchmark continua se houver próxima.':'A vitória será concedida ao outro lado.',rotulo:configuracao.modo==='benchmark'?'Encerrar partida':'Confirmar desistência',acao:desistir});}}><Icone nome="bandeira"/> {configuracao.modo==='benchmark'?'Encerrar partida':'Desistir'}</button>{configuracao.modo==='benchmark'&&configuracao.serie&&<button type="button" onClick={()=>{setMaisAberto(false);setConfirmacao({titulo:'Encerrar o benchmark?',texto:'O benchmark inteiro será interrompido. O placar parcial fica gravado para download manual.',rotulo:'Encerrar benchmark',acao:encerrarBenchmark});}}><Icone nome="fechar"/> Encerrar benchmark</button>}</div>}
    </header>
    {configuracao.serie&&(
      <div className="placar-parcial-serie" role="status" aria-live="polite" aria-label="Placar parcial da série">
        <div className="placar-parcial-meta">
          <small>{configuracao.modo==='benchmark'?'BENCHMARK':'SÉRIE'}</small>
          <strong>Partida {configuracao.serie.indice+1} de {configuracao.serie.total}</strong>
        </div>
        <div className="placar-parcial-placas">
          <div className="placa-competidor">
            <small>A{corDoPapel('A')}</small>
            <strong title={configuracao.serie.rotuloA}>{configuracao.serie.rotuloA}</strong>
            <em className="placar-numero">{configuracao.serie.pontosA}</em>
          </div>
          <div className="placar-parcial-centro">
            <span className="placar-vs">×</span>
            <small>{configuracao.serie.empates} emp.</small>
          </div>
          <div className="placa-competidor placa-competidor-b">
            <small>B{corDoPapel('B')}</small>
            <strong title={configuracao.serie.rotuloB}>{configuracao.serie.rotuloB}</strong>
            <em className="placar-numero">{configuracao.serie.pontosB}</em>
          </div>
        </div>
      </div>
    )}
    <section className="mesa-partida">
      <div className="area-tabuleiro">
        <Relogio nome={nome(corSuperior)} tempo={corSuperior==='w'?tempoBrancas:tempoPretas} ativo={!resultado&&jogo.turn()===corSuperior} cor={corSuperior} semRelogio={semRelogio} pensando={pensando&&jogo.turn()===corSuperior}/>
        <PecasCapturadas cor={corSuperior} pecas={capturasDoLado(material,corSuperior)} vantagem={vantagemDoLado(material,corSuperior)}/>
        <div className="envoltorio-tabuleiro-eval">
          {(analiseLigada||linhasAnalise.length>0)&&(
            <div className="barra-eval" title={formatarEval(evalW.cp,evalW.mate)} aria-label={`Avaliação ${formatarEval(evalW.cp,evalW.mate)}`}>
              <div className="barra-eval-brancas" style={{height:`${pctEval}%`}}/>
              <span className="barra-eval-rotulo">{formatarEval(evalW.cp,evalW.mate)}</span>
            </div>
          )}
          <Tabuleiro jogo={jogo} orientacao={orientacao} selecionada={selecionada} destinos={destinos} ultimaJogada={ultimaJogada} pularAnimacao={pularAnimacao} desabilitado={Boolean(resultado)||pensando||turnoAutomatico} mostrarDestinos={preferencias.mostrarDestinos} setas={setasTabuleiro} aoSelecionar={selecionarCasa} aoMover={moverArrastando}/>
        </div>
        <PecasCapturadas cor={corInferior} pecas={capturasDoLado(material,corInferior)} vantagem={vantagemDoLado(material,corInferior)}/>
        <Relogio nome={nome(corInferior)} tempo={corInferior==='w'?tempoBrancas:tempoPretas} ativo={!resultado&&jogo.turn()===corInferior} cor={corInferior} semRelogio={semRelogio} pensando={pensando&&jogo.turn()===corInferior}/>
        {aviso&&<div className="aviso-partida" role="status"><Icone nome="escudo" tamanho={18}/><span>{aviso}</span><button type="button" aria-label="Fechar aviso" onClick={()=>setAviso(null)}><Icone nome="fechar" tamanho={16}/></button></div>}
      </div>
      <aside className={`painel-partida ${painelMovel?'painel-movel-aberto':''}`}>
        <header><div><small>{rotuloMotor}</small><h2>Livro da partida</h2></div><button type="button" className="fechar-painel-movel" onClick={()=>setPainelMovel(false)} aria-label="Fechar painel"><Icone nome="fechar"/></button></header>
        <div className="placa-estado"><span className={jogo.turn()==='w'?'mini-peca clara':'mini-peca escura'}><Peca tipo="k" cor={jogo.turn()}/></span><div><small>LANCE {Math.floor(historico.length/2)+1}</small><strong>{status}</strong></div></div>
        {configuracao.serie&&(
          <div className="placar-parcial-painel" aria-label="Placar parcial">
            <small>PLACAR PARCIAL · {configuracao.serie.indice+1}/{configuracao.serie.total}</small>
            <div className="placar-parcial-linha">
              <span title={configuracao.serie.rotuloA}><b>A</b> {configuracao.serie.rotuloA}</span>
              <strong>{configuracao.serie.pontosA} – {configuracao.serie.pontosB}</strong>
              <span title={configuracao.serie.rotuloB}><b>B</b> {configuracao.serie.rotuloB}</span>
            </div>
            <em>{configuracao.serie.empates} empate{configuracao.serie.empates===1?'':'s'}</em>
          </div>
        )}
        <div className="ferramentas-analise">
          <button type="button" className={analiseLigada?'botao-analise ativo':''} onClick={()=>setAnaliseLigada(v=>!v)} disabled={pensando&&!analiseLigada}>
            <Icone nome="olho" tamanho={17}/>{analiseLigada?(analisando?'Analisando…':'Análise ligada'):'Analisar'}
          </button>
          <button type="button" onClick={pedirDica} disabled={pensando||Boolean(resultado)}><Icone nome="avancar" tamanho={17}/>Dica</button>
        </div>
        {analiseLigada&&(
          <div className="painel-linhas-analise" aria-live="polite">
            <small>{nomeMotorAnalise?`Motor · ${nomeMotorAnalise}`:'Stockfish'}{analisando?' · a calcular':''}</small>
            {linhasAnalise.length?linhasAnalise.map(linha=>{
              const e=evalBrancas(turnoAtual,linha.avaliacaoCp,linha.mate);
              return <div className="linha-analise" key={`${linha.ordem}-${linha.jogada}`}>
                <b>{formatarEval(e.cp,e.mate)}</b>
                <span>{linha.san||linha.jogada}</span>
                <em>d{linha.profundidade}</em>
              </div>;
            }):<p className="analise-vazia">{analisando?'Calculando linhas…':'Sem linhas ainda.'}</p>}
          </div>
        )}
        <div className="historico-lances" aria-label="Histórico de jogadas">
          {pares.length?<>
            <div className="linha-lance cabecalho-livro"><span>#</span><b><small>{nomeBrancasHist}</small></b><b><small>{nomePretasHist}</small></b></div>
            {pares.map(par=><div className="linha-lance" key={par.numero}><span>{par.numero}.</span><b title={nomeBrancasHist}>{par.brancas?sanParaPortugues(par.brancas.san):''}</b><b title={nomePretasHist}>{par.pretas?sanParaPortugues(par.pretas.san):''}</b></div>)}
          </>:<div className="historico-vazio"><Icone nome="historico" tamanho={30}/><p>O livro será escrito a partir do primeiro lance.</p></div>}
        </div>
        {souJogadorOnline&&(
          <div className="chat-online" aria-label="Chat da partida">
            <header>
              <Icone nome="pessoas" tamanho={16}/>
              <strong>Chat</strong>
              <small>só entre os jogadores</small>
            </header>
            <div className="chat-mensagens">
              {chatMsgs.length===0&&<p className="chat-vazio">Nenhuma mensagem ainda. Combine estratégias ou diga oi.</p>}
              {chatMsgs.map((m)=>(
                <div key={m.id} className={m.autorId===configuracao.onlineClienteId?'chat-msg minha':'chat-msg'}>
                  <strong>{m.autorNome}</strong>
                  <span>{m.texto}</span>
                </div>
              ))}
              <div ref={chatFimRef}/>
            </div>
            <form className="chat-form" onSubmit={(e)=>{e.preventDefault();void enviarChat();}}>
              <input
                value={chatTexto}
                maxLength={280}
                placeholder="Escreva para o adversário…"
                disabled={chatEnviando||Boolean(resultado)}
                onChange={(e)=>setChatTexto(e.target.value)}
              />
              <button type="submit" className="botao-contorno" disabled={chatEnviando||!chatTexto.trim()||Boolean(resultado)}>
                Enviar
              </button>
            </form>
          </div>
        )}
        {souEspectadorOnline&&(
          <p className="nota-espectador">Modo espectador — você não move peças nem usa o chat dos jogadores.</p>
        )}
        <details className="notacao-avancada"><summary>Notação e arquivo</summary>
          <label><span>FEN atual</span><textarea readOnly value={jogo.fen()}/><button type="button" onClick={()=>void copiar(jogo.fen(),'fen')}><Icone nome="copiar" tamanho={17}/>{copiado==='fen'?'Copiado':'Copiar FEN'}</button></label>
          <label><span>PGN da partida</span><textarea readOnly value={jogo.pgn({maxWidth:80,newline:'\n'})}/><span className="acoes-notacao"><button type="button" onClick={()=>void copiar(jogo.pgn({maxWidth:80,newline:'\n'}),'pgn')}><Icone nome="copiar" tamanho={17}/>{copiado==='pgn'?'Copiado':'Copiar PGN'}</button><button type="button" onClick={baixarPgn}><Icone nome="baixar" tamanho={17}/>Baixar</button></span></label>
          <label><span>Importar FEN ou PGN</span><textarea value={textoImport} onChange={e=>setTextoImport(e.target.value)} placeholder="Cole uma posição (FEN) ou partida (PGN)…" rows={3}/><button type="button" onClick={importarPosicao} disabled={pensando}><Icone nome="salvar" tamanho={17}/>Carregar posição</button></label>
        </details>
        <div className={`acoes-partida${configuracao.modo==='benchmark'&&configuracao.serie?' acoes-partida-benchmark':''}`}>
          <button type="button" disabled={duelo||!configuracao.permitirDesfazer||!instantaneos.current.length||pensando} onClick={desfazer}><Icone nome="desfazer"/>Desfazer</button>
          {configuracao.modo==='local'&&<button type="button" onClick={()=>setConfirmacao({titulo:'Declarar empate?',texto:'Confirme que os dois jogadores concordam com o resultado.',rotulo:'Confirmar empate',acao:empate})}><Icone nome="empate"/>Empate</button>}
          <button type="button" className="acao-perigosa" onClick={()=>{
            if(souEspectadorOnline){aoSair();return;}
            setConfirmacao({
            titulo:configuracao.modo==='benchmark'?'Encerrar esta partida?':duelo?'Encerrar confronto?':'Desistir da partida?',
            texto:configuracao.modo==='benchmark'
              ?'Só esta partida será interrompida. O benchmark continua com a próxima, se houver.'
              :duelo?'O duelo ou a partida em andamento será interrompido.':'A vitória será concedida ao outro lado.',
            rotulo:configuracao.modo==='benchmark'?'Encerrar partida':duelo?'Encerrar':'Confirmar desistência',
            acao:desistir
          });}}><Icone nome="bandeira"/>{souEspectadorOnline?'Sair':configuracao.modo==='benchmark'?'Encerrar partida':duelo?'Encerrar':'Desistir'}</button>
          {configuracao.modo==='benchmark'&&configuracao.serie&&(
            <button type="button" className="acao-perigosa acao-encerrar-benchmark" onClick={()=>setConfirmacao({
              titulo:'Encerrar o benchmark?',
              texto:'O benchmark inteiro será interrompido. O placar parcial fica gravado para você baixar se quiser.',
              rotulo:'Encerrar benchmark',
              acao:encerrarBenchmark
            })}><Icone nome="fechar"/>Encerrar benchmark</button>
          )}
        </div>
      </aside>
    </section>
    <nav className="barra-movel-partida" aria-label="Ações da partida"><button type="button" onClick={()=>setOrientacao(o=>o==='w'?'b':'w')}><Icone nome="girar"/><span>Virar</span></button><button type="button" onClick={desfazer} disabled={!configuracao.permitirDesfazer||!instantaneos.current.length}><Icone nome="desfazer"/><span>Desfazer</span></button><button type="button" onClick={()=>setPainelMovel(true)}><Icone nome="historico"/><span>Partida</span></button><button type="button" onClick={()=>setMaisAberto(v=>!v)}><Icone nome="mais"/><span>Mais</span></button></nav>
    {promocao&&<div className="sobreposicao"><section className="modal-promocao" role="dialog" aria-modal="true" aria-labelledby="titulo-promocao"><span>ESCOLHA DA PEÇA</span><h2 id="titulo-promocao">Coroar o peão</h2><p>Qual peça deve ocupar a última fileira?</p><div>{(['q','r','b','n'] as PieceSymbol[]).map(tipo=><button key={tipo} type="button" onClick={()=>realizarMovimento(promocao.de,promocao.para,tipo)} aria-label={`Promover para ${nomesPromocao[tipo]??'peça escolhida'}`}><Peca tipo={tipo} cor={jogo.turn()}/></button>)}</div></section></div>}
    {confirmacao&&<div className="sobreposicao" role="presentation" onMouseDown={e=>{if(e.target===e.currentTarget)setConfirmacao(null);}}><section className="modal-confirmacao" role="dialog" aria-modal="true"><span className="selo-modal"><Icone nome="bandeira"/></span><h2>{confirmacao.titulo}</h2><p>{confirmacao.texto}</p><div><button type="button" className="botao-contorno" onClick={()=>setConfirmacao(null)}>Cancelar</button><button type="button" className="botao-perigo" onClick={()=>{const acao=confirmacao.acao;setConfirmacao(null);acao();}}>{confirmacao.rotulo}</button></div></section></div>}
    {resultado&&<div className="sobreposicao resultado-sobreposicao"><section className="modal-resultado" role="dialog" aria-modal="true" aria-labelledby="titulo-resultado">
      <div className="ornamento-resultado"><i/><span><Icone nome="coroa" tamanho={32}/></span><i/></div>
      <small>PARTIDA CONCLUÍDA</small>
      <h2 id="titulo-resultado">{resultado.titulo}</h2>
      <p>{resultado.motivo}</p>
      <div className="resumo-resultado">
        <span><small>Jogadas</small><strong>{Math.ceil(historico.length/2)}</strong></span>
        <span><small>Ritmo</small><strong>{configuracao.controle.nome.split('·').at(-1)?.trim()??configuracao.controle.nome}</strong></span>
        <span><small>Brancas</small><strong>{nomeBrancasHist}</strong></span>
        <span><small>Pretas</small><strong>{nomePretasHist}</strong></span>
      </div>
      {precisaNomeUsuario&&<label className="campo-regio" style={{marginTop:'1rem',textAlign:'left',width:'100%',maxWidth:320}}>
        <span>Seu nome (para arquivar a partida)</span>
        <input value={nomeUsuario} maxLength={40} placeholder="Como prefere ser chamado?" onChange={e=>setNomeUsuario(e.target.value)}/>
        <small>As partidas com esse nome ficam salvas neste navegador.</small>
      </label>}
      {partidaArquivada&&<p style={{marginTop:'.75rem',opacity:.85}}>Partida arquivada sob o nome <strong>{nomeUsuario.trim()}</strong>.</p>}
      <div className="acoes-resultado">
        {precisaNomeUsuario&&<button type="button" className="botao-principal" onClick={salvarComNome}>Salvar no arquivo</button>}
        <button type="button" className={precisaNomeUsuario?'botao-contorno':'botao-principal'} onClick={aoNovaPartida}>Nova partida</button>
        <button type="button" className="botao-contorno" onClick={sair}>Voltar ao salão</button>
        <button type="button" className="botao-textual" onClick={baixarPgn}><Icone nome="baixar" tamanho={17}/> Baixar PGN</button>
      </div>
    </section></div>}
  </main>;
}
